<template>
    <iframe
        id="gameIframe"
        ref="gameIframe"
        :src="iframeSrc"
        allow="fullscreen;camera;xr-spatial-tracking;autoplay"
        allowusermedia
        class="iframe-box"
        title="Game Server Iframe"
    />
</template>

<script>
    import taskUtil from '@/utils/task-util'
    import emitter from '@/utils/emitter'
    import statsHelper from '@/mixins/statsHelper'

    export default {
        name: 'Game',
        mixins: [statsHelper],
        props: {
            freeGame: {
                type: Object,
                required: false,
                default: null
            }
        },
        data() {
            return {
                actionStatus: null,
                lastGameScore: 0
            }
        },
        computed: {
            isPlayAgainEnabled() {
                return this.$store.getters.isPlayAgainEnabled
            },
            task() {
                let missionId = parseInt(this.$route.params.missionId)
                let taskId = parseInt(this.$route.params.taskId)
                return this.$store.getters.getTask(missionId, taskId)
            },
            mission() {
                return this.$store.getters.getMission(parseInt(this.$route.params.missionId))
            },
            scanCode() {
                return this.$store.getters.getScanCode
            },
            lang() {
                return this.$store.getters.getLanguage
            },
            iframeSrc() {
                const gameId = this.freeGame ? null : this.task?.id

                const apiToken = localStorage.getItem(`${process.env.VUE_APP_API_HOST_DOMAIN}-web`)

                if (this.locationPath === this.config.host) {
                    const url = `${this.config.gameServerHost}/bond/game?targetOrigin=${this.config.host}&lang=${this.lang}&isChallenge=false&gameId=${gameId}&accessToken=${apiToken}`
                    return apiToken ? url : `${this.config.gameServerHost}/bond/game?targetOrigin=${this.config.host}&lang=${this.lang}&isChallenge=false`
                } else {
                    const url = `${this.config.gameServerHost}/bond/game?targetOrigin=${this.locationPath}&apiHost=${this.config.host}&lang=${this.lang}&isChallenge=false&gameId=${gameId}&accessToken=${apiToken}`
                    return apiToken ? url : `${this.config.gameServerHost}/bond/game?targetOrigin=${this.locationPath}&apiHost=${this.config.host}&lang=${this.lang}&isChallenge=false`
                }
            },

            locationPath() {
                return window.location.host.includes('localhost')
                    ? 'http://localhost:8080'
                    : window.location.protocol + '//' + window.location.host
            },
            isLuckyGame() {
                return this.task?.lucky || this.freeGame?.task?.lucky
            },
            isGameScratch() {
                return this.task?.taskType?.alias === 'GAME_SCRATCH' || this.freeGame?.task?.taskType?.alias === 'GAME_SCRATCH'
            },
            isGameSlot() {
                return this.task?.taskType?.alias === 'GAME_SLOT_MACHINE' || this.freeGame?.task?.taskType?.alias === 'GAME_SLOT_MACHINE'
            }

        },
        created() {
            window.addEventListener('message', this.messageEventHandler)
        },
        beforeDestroy() {
            this.exitFullscreen()
            this.closeFrame()
            window.removeEventListener('message', this.messageEventHandler)
        },
        mounted() {
            const vh = window.innerHeight * 0.01
            document.documentElement.style.setProperty('--vh', `${vh}px`)
            window.addEventListener('resize', () => {
                const vh = window.innerHeight * 0.01
                document.documentElement.style.setProperty('--vh', `${vh}px`)
            })

            const element = document.getElementById('gameIframe')
            element.scrollIntoView({ block: 'end', inline: 'nearest' })
        },
        methods: {
            messageEventHandler(event) {
                if (!this.config.gameServerHost || !this.task) {
                    return
                }

                const json = event.data
                switch (json.action) {
                case 'finish':
                    this.handleFinishAction(json, event.data?.payload)
                    break
                case 'load-completed':
                    this.handleLoadCompletedAction(json)
                    break
                case 'rotateEnded':
                    this.handleRotateEndedAction(json)
                    break
                case 'start':
                    this.handleStartAction(json)
                    break
                case 'close':
                    this.handleCloseAction()
                    break
                case 'fullscreen':
                    this.handleFullscreenAction()
                    break
                case 'close-on-action':
                    this.handleCloseAction()
                    break
                default:
                    console.warn('Unknown game message action:', json.action)
                }
            },
            async handleCloseAction() {
                if (this.mission && this.task) {
                    const isPlayAgain = this.task.completed
                    await taskUtil.executePostUpdateActions(this.mission, this.task, this.lastGameScore, this.$router, null, isPlayAgain)
                }
                this.closeFrame()
            },

            async drawPrizeAndPostMessage() {
                try {
                    const response = await this.$store.dispatch('drawPrize', {
                        missionId: this.mission?.id ?? this.freeGame?.missionId,
                        taskId: this.task.id ?? this.freeGame?.task.id
                    })
                    let prizeId = response?.data?.id

                    const zeroPointGames = ['GAME_SCRATCH', 'GAME_SLOT_MACHINE']

                    if (zeroPointGames.includes(this.task?.taskType?.alias) && response?.data?.points === 0) {
                        prizeId = -1
                    }

                    this.$refs.gameIframe.contentWindow.postMessage({
                        action: this.task?.taskType?.alias ? this.getAction(this.task.taskType.alias) : this.getAction(this.freeGame?.task?.taskType?.alias),
                        prizeId,
                        isPlayAgain: (this.isPlayAgainEnabled && this.task.completed) || !!this.freeGame
                    }, '*')
                } catch (error) {
                    console.error(error)
                }
            },

            async handleFinishAction(json, payload = null) {
                this.actionStatus = 'FINISHED'
                this.lastGameScore = json.score
                emitter.emit('killTimer', { taskId: this.task.id })
                if (this.freeGame) {
                    this.storeFreeTaskInfo(this.freeGame, json.score)
                    if (this.scanCode) {
                        this.scanQrSpot(this.scanCode, 'QR_SCAN', 'FREE_GAME_PLAYED')
                    }
                    return
                }
                if (!this.task.completed) {
                    const resultsResponse = await taskUtil.updateTask(this.mission, this.task, json.score, true, this.$router, payload)

                    if (resultsResponse) {
                        this.lastGameScore = resultsResponse.score
                        this.sendResults(resultsResponse.results, this.lastGameScore, resultsResponse.correctAnswers)
                    }
                } else {
                    await taskUtil.executePostUpdateActions(this.mission, this.task, this.lastGameScore, this.$router, null, true)
                }
            },
            sendResults(results, score, correctAnswers) {
                this.$refs.gameIframe.contentWindow.postMessage({
                    action: 'quiz-results',
                    results,
                    score,
                    correctAnswers
                }, '*')
            },
            handleLoadCompletedAction(json) {
                if (this.isGameScratch) {
                    this.drawPrizeAndPostMessage(json)
                }
            },

            handleRotateEndedAction() {
                if (this.isLuckyGame) {
                    this.actionStatus = 'ROTATE_ENDED'
                }
            },

            handleStartAction(json) {
                if (this.actionStatus === 'STARTED') {
                    return
                }
                this.actionStatus = 'STARTED'
                emitter.emit('startTimer', { taskId: this.task.id, duration: this.task.duration, isTonyGame: true })
                if (!this.task.lucky && !this.freeGame?.task?.lucky) {
                    return
                }
                this.drawPrizeAndPostMessage(json)
            },

            handleFullscreenAction() {
                this.enterFullscreen()
            },

            getAction(taskType) {
                if (taskType === 'GAME_LUCKY_WHEEL' || taskType === 'GAME_ENHANCED_LUCKY_WHEEL') {
                    return 'lucky-wheel-spin'
                } else if (taskType === 'GAME_SCRATCH') {
                    return 'scratch-start'
                } else if (taskType === 'GAME_SLOT_MACHINE') {
                    return 'slot-spin'
                }
            },
            closeFrame() {
                const frame = this.$refs.gameIframe
                if (frame) {
                    frame.remove()
                }
            },
            storeFreeTaskInfo(freeGameDTO, score) {
                emitter.emit('openFreeGameModal', { freeGameDTO, score })
                this.$store.commit('setFreeTaskInfo', { freeGameDTO, score })
            },
            enterFullscreen() {
                const gameElement = this.$refs.gameIframe
                if (gameElement.requestFullscreen) {
                    gameElement.requestFullscreen()
                } else if (gameElement.webkitRequestFullscreen) {
                    gameElement.webkitRequestFullscreen()
                } else if (gameElement.msRequestFullscreen) {
                    gameElement.msRequestFullscreen()
                } else if (gameElement.webkitEnterFullscreen) {
                    gameElement.webkitEnterFullscreen()
                }
            },

            exitFullscreen() {
                try {
                    const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement
                    if (isFullscreen) {
                        if (document.exitFullscreen) {
                            document.exitFullscreen()
                        } else if (document.webkitExitFullscreen) {
                            document.webkitExitFullscreen()
                        } else if (document.msExitFullscreen) {
                            document.msExitFullscreen()
                        }
                    }
                } catch {
                }
            }
        }
    }
</script>
<style scoped>
.iframe-box {
    border: none;
    height: 100vh;
    width: 100%;
}
</style>
